import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowUpRight, BookOpen, Check, Copy, Gauge, Globe2, Image, Moon, SlidersHorizontal, Sun, Terminal } from "lucide-react";
import { ICON_CONFIG } from "../config";
import { toolExamples, updates } from "../data/updates";
import "../App.css";
import "./UpdatesPage.css";

const featureIcons = [Globe2, Image, SlidersHorizontal];

export default function UpdatesPage({ isDark = false, onThemeToggle, onBack, onDocs, backLabel = "返回对话" }) {
  const [copied, setCopied] = useState(null);
  const [copyError, setCopyError] = useState(null);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copyExample = async (example) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(example.payload, null, 2));
      setCopied(example.id);
      setCopyError(null);
    } catch {
      setCopyError(example.id);
    }
  };

  return (
    <div className={`updates-page ${isDark ? "theme-dark" : "theme-light"}`}>
      <header className="updates-header">
        <a className="updates-brand" href="/" aria-label="LumaNova 首页" onClick={(event) => {
          if (onBack && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onBack(); }
        }}>
          <img src={ICON_CONFIG.logo} alt="" width="28" height="28" />
          <span>LumaNova</span>
        </a>
        <nav aria-label="页面导航">
          <a className="updates-back" href="/docs" onClick={event => { if (onDocs && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onDocs(); } }}>开发文档</a>
          <button type="button" className="updates-back" onClick={onBack}><ArrowLeft size={16} />{backLabel}</button>
          <button type="button" className="updates-icon" onClick={onThemeToggle}
            aria-label={isDark ? "切换浅色模式" : "切换深色模式"} title={isDark ? "切换浅色模式" : "切换深色模式"}>
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </nav>
      </header>

      <main className="updates-main">
        <div className="updates-intro">
          <p className="updates-eyebrow">LUMANOVA / WHAT'S NEW</p>
          <h1>更新公告</h1>
          <p className="updates-lead">新能力、产品改进，以及值得你知道的变化。</p>
          <div className="updates-index"><span className="updates-live-dot" />持续更新<span className="updates-count">{updates.length} 条记录</span></div>
        </div>

        {updates.map((update, index) => (
          <article className="update-entry" key={update.id} id={update.id}>
            <aside className="update-meta" aria-label="更新信息">
              <time dateTime={update.date}>{update.date}</time>
              <div><span className="update-category">{update.category}</span>{index === 0 && <span className="update-latest">最新</span>}</div>
              <a href={`#${update.id}`} className="update-permalink" aria-label="本条更新的固定链接" title="本条更新的固定链接"><ArrowUpRight size={17} /></a>
            </aside>
            <div className="update-body">
              <p className="update-kicker">{update.kicker || "内置工具 · 正式上线"}</p>
              <h2>{update.title}</h2>
              <p className="update-summary">{update.summary}</p>
              {update.docsSection && <a className="update-docs-link" href={`/docs#${update.docsSection}`} onClick={event => { if (onDocs && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onDocs(update.docsSection); } }}>阅读完整接入指南<ArrowUpRight size={16} /></a>}

              <section className="update-section" aria-label="新增能力">
                {update.features.map((feature, featureIndex) => {
                  const Icon = (update.id === "codex-and-developer-docs" ? [Terminal, BookOpen, Gauge] : featureIcons)[featureIndex] || BookOpen;
                  return <div className="update-feature" key={feature.name}>
                    <Icon size={19} aria-hidden="true" />
                    <div><h3>{feature.name}</h3><p>{feature.description}</p></div>
                  </div>;
                })}
              </section>

              <section className="update-section">
                <h3 className="update-section-title">{update.sectionTitle || "接入方式"}</h3>
                <ul className="update-notes">{update.notes.map((note) => <li key={note}>{note}</li>)}</ul>
                {update.id === "responses-hosted-tools" && <><div className="update-endpoint">
                  <code>POST https://api.lumanova.icu/v1/responses</code>
                  <p>三个工具使用同一个接口。请求头携带你自己的 Luma API Key：</p>
                  <pre><code>{'Authorization: Bearer <LUMA_API_KEY>\nContent-Type: application/json\nIdempotency-Key: <本次请求的唯一 UUID>'}</code></pre>
                  <p>model 填支持工具调用的语言模型；用 tools 声明允许的工具。tool_choice 为 auto 时由模型选择，指定工具类型时要求调用对应工具。</p>
                </div>
                {toolExamples.map((example) => <details className="update-example" key={example.id}>
                  <summary>{example.name}调用示例<span>{example.label}</span></summary>
                  <p className="update-example-note">{example.description}</p>
                  <div className="update-code-header"><span>JSON</span>
                    <button type="button" className="updates-icon" onClick={() => copyExample(example)}
                      aria-label={copied === example.id ? `${example.name}已复制` : `复制${example.name}示例`}
                      title={copied === example.id ? "已复制" : `复制${example.name}示例`}>
                      {copied === example.id ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                  <pre><code>{JSON.stringify(example.payload, null, 2)}</code></pre>
                  <span role="status" className="update-copy-status">{copied === example.id ? "已复制" : copyError === example.id ? "复制失败，请选取示例内容。" : ""}</span>
                  <p className="update-example-note">{example.result}</p>
                </details>)}
                <div className="update-response-notes">
                  <h4>继续对话与结果处理</h4>
                  <p>请先检查顶层 status：completed 为完成，failed 为失败；同时读取 error、usage 和 billing。任务标识使用 request_id，幂等键不是任务编号。</p>
                  <p>继续编辑时，把历史消息及图片 URL 一起放入本次 input，或直接提供新的参考图。接口不替调用方保存会话，也不能用 previous_response_id 自动取回历史。</p>
                  <p>生成图片可能耗时较长。示例使用 stream: false 一次性返回；长任务可改用 stream: true，但客户端需要按 SSE 接收进度、心跳与最终响应，不能直接按单个 JSON 读取。</p>
                  <p>网络断开不代表任务失败。请保留相同幂等键，勿换新键自动重试；重复提交可能返回 409 和原任务 request_id，当前不自动重放原结果。</p>
                </div></>}
              </section>

              <section className="update-section update-billing">
                <h3 className="update-section-title">用量与失败处理</h3>
                <ul className="update-notes">{update.billing.map((note) => <li key={note}>{note}</li>)}</ul>
              </section>
            </div>
          </article>
        ))}
        <footer className="updates-footer"><span>LumaNova</span><span>每一步更新，都在这里。</span></footer>
      </main>
    </div>
  );
}
